/**
 * Program-level volume summary (coach-only).
 * Shows a weekly matrix: rows = muscle groups present, columns = days of week + Totale.
 * Weekly thresholds (Schoenfeld): basso <10, medio 10-19, alto ≥20.
 */
import { useState } from "react";
import axios from "axios";
import { CaretDown, PencilSimple, Check } from "@phosphor-icons/react";
import {
  getEffectiveGroups, DAYS_OF_WEEK, buildLibraryMap, tallyWorkout,
  weeklyVolumeLevel, LEVEL_STYLES, formatVolume,
} from "./muscleVolume";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProgramVolumeSummary({ workouts, exerciseLibrary, userMappings, onLibraryUpdated }) {
  const [unmappedOpen, setUnmappedOpen] = useState(false);
  const [savingName, setSavingName] = useState(null);

  if (!workouts || workouts.length === 0) return null;

  const libMap = buildLibraryMap(exerciseLibrary, userMappings);
  const groupsAll = getEffectiveGroups(userMappings);
  const groupLabels = Object.fromEntries(groupsAll.map((g) => [g.key, g.label]));
  const tallyOpts = {
    countSecondary: userMappings?.count_secondary !== false,
    secondaryWeight: typeof userMappings?.secondary_weight === "number"
      ? userMappings.secondary_weight : 0.5,
  };

  // Build matrix: matrix[dayKey || "_unassigned"][groupKey] = sets
  const matrix = {};
  const totalPerGroup = {};

  workouts.forEach((w) => {
    const dayKey = w.day_of_week || "_unassigned";
    if (!matrix[dayKey]) matrix[dayKey] = {};
    const { tally } = tallyWorkout(w.exercises || [], libMap, tallyOpts);
    Object.entries(tally).forEach(([group, sets]) => {
      matrix[dayKey][group] = (matrix[dayKey][group] || 0) + sets;
      totalPerGroup[group] = (totalPerGroup[group] || 0) + sets;
    });
  });

  const groupsPresent = groupsAll.filter((g) => (totalPerGroup[g.key] || 0) > 0);

  // --- Unmapped exercises (by name, deduped) ---
  const unmappedMap = new Map(); // name_lower → { name, totalSets, libraryId|null }
  workouts.forEach((w) => {
    (w.exercises || []).forEach((ex) => {
      if (!ex?.name) return;
      const key = ex.name.trim().toLowerCase();
      const entry = libMap[key];
      if (entry && entry.primary) return; // already mapped
      const lib = (exerciseLibrary || []).find((l) => l.name?.trim().toLowerCase() === key);
      const sets = parseInt(ex.sets, 10) || 0;
      const prev = unmappedMap.get(key);
      unmappedMap.set(key, {
        name: ex.name,
        totalSets: (prev?.totalSets || 0) + sets,
        libraryId: lib?.id || null,
      });
    });
  });
  const unmappedList = [...unmappedMap.values()].sort((a, b) => b.totalSets - a.totalSets);

  const saveMapping = async (item, groupLabel) => {
    if (!groupLabel) return;
    setSavingName(item.name);
    try {
      if (item.libraryId) {
        await axios.put(`${API}/exercises/${item.libraryId}`, { muscle_group: groupLabel });
      } else {
        await axios.post(`${API}/exercises`, { name: item.name, muscle_group: groupLabel });
      }
      if (typeof onLibraryUpdated === "function") await onLibraryUpdated();
    } catch { /* noop */ } finally {
      setSavingName(null);
    }
  };

  if (groupsPresent.length === 0 && unmappedList.length === 0) return null;

  const daysWithWorkouts = DAYS_OF_WEEK.filter((d) =>
    workouts.some((w) => w.day_of_week === d.key)
  );
  const hasUnassigned = workouts.some((w) => !w.day_of_week);

  const visibleDays = [
    ...daysWithWorkouts.map((d) => ({ key: d.key, label: d.short })),
    ...(hasUnassigned ? [{ key: "_unassigned", label: "?" }] : []),
  ];

  return (
    <div
      className="mt-3 rounded-xl border border-[#1E1E1E] bg-[#0A0A0A]/60 p-3"
      data-testid="program-volume-summary"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">
            Volume settimanale programma
          </span>
          <span
            className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#D4FF00]/10 text-[#D4FF00]"
            title="Visibile solo al coach"
          >
            Coach
          </span>
        </div>
        {unmappedList.length > 0 && (
          <button
            type="button"
            onClick={() => setUnmappedOpen((o) => !o)}
            className="text-[9px] flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-[#FF9500]/30 bg-[#FF9500]/10 text-[#FF9500] hover:bg-[#FF9500]/20 transition-colors"
            data-testid="program-volume-unmapped-toggle"
            title="Clicca per mappare gli esercizi mancanti"
          >
            <PencilSimple size={9} weight="fill" />
            <span className="font-semibold tabular-nums">{unmappedList.length}</span>
            <span>non mappati</span>
            <CaretDown size={9} className={`transition-transform ${unmappedOpen ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>

      {/* Quick-edit panel for unmapped exercises */}
      {unmappedOpen && unmappedList.length > 0 && (
        <div
          className="mb-3 rounded-lg border border-[#FF9500]/25 bg-[#FF9500]/5 p-2.5 space-y-1.5"
          data-testid="program-volume-unmapped-panel"
        >
          <p className="text-[10px] text-[#FF9500] font-semibold tracking-wide mb-1.5">
            Assegna un gruppo muscolare per includerli nel volume
          </p>
          {unmappedList.map((item) => {
            const isSaving = savingName === item.name;
            return (
              <div
                key={item.name}
                className="flex items-center gap-2 bg-[#0A0A0A] border border-[#1E1E1E] rounded-md px-2 py-1.5"
                data-testid={`unmapped-row-${item.name}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium text-white truncate">{item.name}</p>
                  <p className="text-[9px] text-zinc-600">
                    {item.totalSets} {item.totalSets === 1 ? "serie" : "serie"} · {item.libraryId ? "in libreria" : "non in libreria"}
                  </p>
                </div>
                <select
                  disabled={isSaving}
                  defaultValue=""
                  onChange={(e) => saveMapping(item, e.target.value)}
                  className="bg-[#171717] border border-[#262626] rounded-md text-[11px] text-white outline-none focus:border-[#FF9500] px-1.5 py-1"
                  data-testid={`unmapped-select-${item.name}`}
                >
                  <option value="" disabled>Scegli gruppo…</option>
                  {groupsAll.map((g) => (
                    <option key={g.key} value={g.label}>{g.label}</option>
                  ))}
                </select>
                {isSaving && (
                  <div
                    className="w-3 h-3 border-2 border-[#FF9500] border-t-transparent rounded-full animate-spin"
                    data-testid={`unmapped-saving-${item.name}`}
                  />
                )}
                {!isSaving && savingName === null && (
                  <span className="w-3 h-3 flex items-center justify-center opacity-0 pointer-events-none">
                    <Check size={10} weight="bold" />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Totals row — prominent */}
      {groupsPresent.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-[#1E1E1E]">
          {groupsPresent.map((g) => {
            const total = totalPerGroup[g.key] || 0;
            const level = weeklyVolumeLevel(total);
            const s = LEVEL_STYLES[level] || LEVEL_STYLES.basso;
            return (
              <div
                key={g.key}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full ring-1 ${s.bg} ${s.ring}`}
                data-testid={`program-volume-${g.key}`}
                data-level={level}
                title={`${g.label}: ${formatVolume(total)} serie settimanali (${level})`}
              >
                <span className={`text-[10px] font-semibold ${s.text}`}>{g.label}</span>
                <span className={`text-[10px] font-bold tabular-nums ${s.text}`}>{formatVolume(total)}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Matrix — per day breakdown */}
      {groupsPresent.length > 0 && visibleDays.length > 0 && (
        <div className="overflow-x-auto" data-testid="program-volume-matrix">
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className="text-[9px] font-bold uppercase tracking-wider text-zinc-600 pb-1.5 pr-2 sticky left-0 bg-[#0A0A0A]/80 backdrop-blur-sm">
                  Gruppo
                </th>
                {visibleDays.map((d) => (
                  <th
                    key={d.key}
                    className="text-[9px] font-bold uppercase tracking-wider text-zinc-600 pb-1.5 px-1 text-center"
                    data-testid={`program-volume-day-${d.key}`}
                  >
                    {d.label}
                  </th>
                ))}
                <th className="text-[9px] font-bold uppercase tracking-wider text-[#D4FF00] pb-1.5 pl-2 text-right">
                  Tot
                </th>
              </tr>
            </thead>
            <tbody>
              {groupsPresent.map((g) => {
                const total = totalPerGroup[g.key] || 0;
                const level = weeklyVolumeLevel(total);
                const s = LEVEL_STYLES[level] || LEVEL_STYLES.basso;
                return (
                  <tr
                    key={g.key}
                    className="border-t border-[#1E1E1E]/60"
                    data-testid={`program-volume-row-${g.key}`}
                  >
                    <td className="text-[11px] font-medium text-zinc-300 py-1.5 pr-2 sticky left-0 bg-[#0A0A0A]/80 backdrop-blur-sm">
                      {groupLabels[g.key] || g.label}
                    </td>
                    {visibleDays.map((d) => {
                      const cell = matrix[d.key]?.[g.key] || 0;
                      return (
                        <td
                          key={d.key}
                          className="py-1.5 px-1 text-center text-[11px] tabular-nums"
                          data-testid={`program-volume-cell-${g.key}-${d.key}`}
                        >
                          {cell > 0 ? (
                            <span className="text-white font-medium">{formatVolume(cell)}</span>
                          ) : (
                            <span className="text-zinc-700">-</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="py-1.5 pl-2 text-right">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${s.bg} ${s.text}`}>
                        {formatVolume(total)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      {groupsPresent.length > 0 && (
        <div className="flex items-center gap-3 mt-3 pt-2 border-t border-[#1E1E1E]">
          <LegendDot level="basso" label="Basso (<10/sett)" />
          <LegendDot level="medio" label="Medio (10-19)" />
          <LegendDot level="alto" label="Alto (≥20)" />
        </div>
      )}
    </div>
  );
}

function LegendDot({ level, label }) {
  const dotColor =
    level === "alto" ? "bg-[#D4FF00]"
      : level === "medio" ? "bg-[#007AFF]"
        : "bg-zinc-400";
  return (
    <div className="flex items-center gap-1" data-testid={`program-legend-${level}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
      <span className="text-[9px] text-zinc-600">{label}</span>
    </div>
  );
}
